import { forwardRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { isHomeOrganization } from '../../../utils/homeOrganization';
import './AttendanceLogPdfCanvas.css';

const HOME_COMPANY_FOOTER = 'Pró Médico Ltda — Material Médico-Hospitalar | Pelotas/RS';

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

type DailyLogRow = {
  isoDate: string;
  date: string;
  weekday: string;
  entries: { in1: string; out1: string; in2: string; out2: string };
  total: string;
  balance: string;
  status: 'normal' | 'extra' | 'delay' | 'absent' | 'weekend';
  signed: boolean;
  certificateHours?: number;
  hasFullCertificate?: boolean;
  hasPartialCertificate?: boolean;
};

type EmployeeInfo = {
  name: string;
  department?: string;
  id: number;
};

export interface AttendanceLogPdfPayload {
  employee: EmployeeInfo;
  logs: DailyLogRow[];
  month: number;
  year: number;
}

function formatCertificateMinutes(minutes?: number) {
  if (!minutes) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

function statusLabel(status: DailyLogRow['status']) {
  switch (status) {
    case 'normal': return 'Normal';
    case 'extra': return 'Hora Extra';
    case 'delay': return 'Atraso';
    case 'absent': return 'Falta';
    case 'weekend': return 'DSR / Folga';
    default: return status;
  }
}

function certificateLabel(log: DailyLogRow) {
  if (log.hasFullCertificate) return 'Total';
  if (log.hasPartialCertificate) return 'Parcial';
  return '—';
}

function companyInitials(name: string) {
  const skip = new Set(['ltda', 'me', 'eireli', 'sa', 's.a', 'de', 'da', 'do', 'dos', 'das']);
  const words = name
    .split(/\s+/)
    .map((word) => word.replace(/[^A-Za-zÀ-ÿ0-9]/g, ''))
    .filter((word) => word && !skip.has(word.toLowerCase()));
  const letters = words.slice(0, 2).map((word) => word[0].toUpperCase());
  return letters.join('') || 'WF';
}

const AttendanceLogPdfCanvas = forwardRef<HTMLDivElement, AttendanceLogPdfPayload>(
  function AttendanceLogPdfCanvas({ employee, logs, month, year }, ref) {
    const { organizations, currentSchema, user } = useAuth() as {
      organizations?: Array<{ schema_name?: string; label?: string; is_home?: boolean }>;
      currentSchema?: string | null;
      user?: { organization_schema?: string; organization_label?: string } | null;
    };
    const schema = currentSchema || user?.organization_schema || 'public';
    const organization = (organizations || []).find((item) => item.schema_name === schema);
    const companyName = organization?.label || user?.organization_label || 'Organização';
    const companyFooter = isHomeOrganization(organizations, schema) ? HOME_COMPANY_FOOTER : companyName;
    const issuedAt = new Date().toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
    const periodLabel = `${MONTH_NAMES[month - 1] || month} ${year}`;
    const signedCount = logs.filter((log) => log.signed).length;

    return (
      <div ref={ref} className="al-pdf-canvas">
        <header className="al-pdf-header">
          <div className="al-pdf-brand">
            <div className="al-pdf-logo">{companyInitials(companyName)}</div>
            <div>
              <p className="al-pdf-company-name">{companyName}</p>
              <p className="al-pdf-company-sub">Controle de ponto</p>
            </div>
          </div>
          <div className="al-pdf-title-block">
            <h1 className="al-pdf-title">Espelho de Ponto</h1>
            <p className="al-pdf-meta">Emissão: {issuedAt}</p>
            <p className="al-pdf-meta">{logs.length} dia(s) no período</p>
          </div>
        </header>

        <div className="al-pdf-body">
          <div className="al-pdf-parties">
            <div>
              <p className="al-pdf-party-label">Colaborador</p>
              <p className="al-pdf-party-name">{employee.name}</p>
              <p className="al-pdf-party-detail">
                ID #{String(employee.id).padStart(4, '0')}
                {employee.department ? ` · ${employee.department}` : ''}
              </p>
            </div>
            <div>
              <p className="al-pdf-party-label">Período</p>
              <p className="al-pdf-party-name">{periodLabel}</p>
              <p className="al-pdf-party-detail">Assinaturas: {signedCount} de {logs.length}</p>
            </div>
          </div>

          <table className="ar-pdf-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Dia</th>
                <th className="center">Entrada 1</th>
                <th className="center">Saída 1</th>
                <th className="center">Entrada 2</th>
                <th className="center">Saída 2</th>
                <th className="center">Total</th>
                <th className="center">Banco</th>
                <th className="center">Atestado</th>
                <th className="center">Status</th>
                <th className="center">Assinatura</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.isoDate} className={log.status === 'weekend' ? 'weekend' : undefined}>
                  <td>{log.date}</td>
                  <td>{log.weekday}</td>
                  <td className="center al-pdf-cell-mono">{log.entries.in1}</td>
                  <td className="center al-pdf-cell-mono">{log.entries.out1}</td>
                  <td className="center al-pdf-cell-mono">{log.entries.in2}</td>
                  <td className="center al-pdf-cell-mono">{log.entries.out2}</td>
                  <td className="center al-pdf-cell-mono">
                    {log.total}
                    {(log.certificateHours || 0) > 0 && (
                      <span className="al-pdf-cert-note">
                        +{formatCertificateMinutes(log.certificateHours)} atestado
                      </span>
                    )}
                  </td>
                  <td className="center al-pdf-cell-mono">
                    <span
                      className={`al-pdf-balance ${
                        log.balance.startsWith('+')
                          ? 'positive'
                          : log.balance.startsWith('-')
                            ? 'negative'
                            : ''
                      }`}
                    >
                      {log.balance}
                    </span>
                  </td>
                  <td className="center">{certificateLabel(log)}</td>
                  <td className="center">
                    <span className={`al-pdf-status ${log.status}`}>{statusLabel(log.status)}</span>
                  </td>
                  <td className="center">{log.signed ? 'Assinado' : 'Pendente'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="ar-pdf-summary">
            <div className="al-pdf-summary-box">
              <span>Colaborador: </span>
              <strong>{employee.name}</strong>
              <span> · Período: </span>
              <strong>{periodLabel}</strong>
            </div>
          </div>

          <footer className="ar-pdf-footer">{companyFooter}</footer>
        </div>
      </div>
    );
  }
);

export default AttendanceLogPdfCanvas;
